import { authorBaseUrl } from "@/constants/index"
import {
  isTeaser,
  extractTeaserData,
  mapCards,
} from "@/components/DesignSystem/utils/teaserHelper"
import { sanitizeInnerHtml } from "@/utils/helper"
import { modifyHref } from "@/components/Shared/HeaderNavTabs/utils"

/**
 * Maps two-up content container and its teaser children to props for the component.
 *
 * @param {Object} container - The container object, possibly with style/class info.
 * @param {Array} children - The child components, expected to include teasers.
 * @return {Object} Mapped props including heading, description, cards, and background.
 */
export function mapTwoUpContentProps(container, children) {
  // Defensive: filter only valid teasers
  const teasers = children.filter(isTeaser)

  // Explicitly identify main and card teasers
  const mainTeaser = teasers.length > 0 ? teasers[0] : {}
  const potentialCardTeasers = teasers.slice(1)

  if (teasers.length < 3) {
    // Correctly include the count in the warning
    console.warn(
      `[TwoUpContent Mapper] Expected at least 3 teasers (1 main + 2 cards), got ${teasers.length}. Container:`,
      container.containerId || container
    )
  }

  // Take the first two potential cards
  const cardTeasers = potentialCardTeasers.slice(0, 2)

  // If less than 2 card teasers found, fill with empty objects
  while (cardTeasers.length < 2) cardTeasers.push({})

  // Map data using the identified teasers
  const mainTeaserData = extractTeaserData(mainTeaser)

  const {
    ctaLabel = "",
    description = "",
    linkUrl = "",
    title = "",
  } = mainTeaserData

  const cards = cardTeasers.map(extractTeaserData)

  // Optionally, map container-level styles

  const colors = container.appliedCssClassNames
    ? container.appliedCssClassNames.match(/\bbg-([a-z]+)\b/)
    : null

  const background = colors ? colors[1] : null
  const pageType = mainTeaser?.pageType || ""
  const cardsArray = mapCards(cards, pageType, authorBaseUrl)

  return {
    mod: background,
    type: "50-50",
    imageOrientation: "landscape",
    heading: {
      text: title,
    },

    description: {
      text: description,
    },
    CTA: {
      href: modifyHref(linkUrl),
      text: ctaLabel,
      trackingAttributes: {
        "data-datalayer-id": "kds-element-navigation",
        "data-datalayer-data": JSON.stringify({
          clickInternalLinks: "true",
          eventAction: `${pageType}:${sanitizeInnerHtml(
            title
          ).toLowerCase()}:${sanitizeInnerHtml(ctaLabel)?.toLowerCase()}`,
          eventName: `${pageType}:${sanitizeInnerHtml(
            title
          )?.toLowerCase()}:${sanitizeInnerHtml(ctaLabel)?.toLowerCase()}`,
          eventType: "navigation",
          internalLinkName: sanitizeInnerHtml(ctaLabel)?.toLowerCase(),
          internalLinkPosition: `${pageType}:teaser`,
          internalLinkType: `${pageType}:${sanitizeInnerHtml(
            title
          )?.toLowerCase()}`,
          internalLinkURL:
            linkUrl && typeof window !== "undefined"
              ? `${window.location.origin}${linkUrl
                  ?.split(process.env.NEXT_PUBLIC_INTERNALLINKPATH)
                  .join("")
                  .replace(".html", "")}`
              : "n/a",
          internalLinkZoneName: `${pageType}:${sanitizeInnerHtml(
            title
          )?.toLowerCase()}`,
          eventMsg: "n/a",
          eventStatus: "n/a",
        }),
      },
    },
    cards: cardsArray,
  }
}
